import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import DancerThumbnail from './DancerThumbnail';
import MergeScriptsTab from "./MergeScriptsTab";
import "./MergeDancers.css";

export default function MergeDancers() {
  const [allDancers, setAllDancers] = useState([]);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [selectedDancers, setSelectedDancers] = useState([]);
  const [groupSelections, setGroupSelections] = useState({}); // groupIndex → Set of dancer names
  const [mergeStatus, setMergeStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(true);
  const [activeTab, setActiveTab] = useState("merge"); // "merge" | "scripts" | "unmerge"
  const [mergeHistory, setMergeHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadAllDancers();
  }, []);

  // Load merge history when switching to unmerge tab
  useEffect(() => {
    if (activeTab === "unmerge") loadMergeHistory();
  }, [activeTab]);

  async function loadMergeHistory() {
    setHistoryLoading(true);
    try {
      const q = query(collection(db, "mergeHistory"), orderBy("mergedAt", "desc"));
      const snap = await getDocs(q);
      setMergeHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error loading merge history:", e);
    }
    setHistoryLoading(false);
  }

  // 🎭 Normalize name for comparison (handle case, order, spaces)
  function normalizeName(name) {
    if (!name) return "";
    
    // Remove extra spaces and convert to lowercase
    const cleaned = name.trim().toLowerCase();
    
    // Split into words and sort them to handle name order differences
    const words = cleaned.split(/\s+/).sort();
    
    return words.join(" ");
  }

  // � Calculate Levenshtein distance between two strings
  function levenshteinDistance(str1, str2) {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  // 🔢 Calculate similarity percentage between two names
  function calculateSimilarity(name1, name2) {
    if (!name1 || !name2) return 0;
    
    const norm1 = normalizeName(name1);
    const norm2 = normalizeName(name2);
    
    if (norm1 === norm2) return 100;
    
    const distance = levenshteinDistance(norm1, norm2);
    const maxLength = Math.max(norm1.length, norm2.length);
    
    if (maxLength === 0) return 100;
    
    const similarity = ((maxLength - distance) / maxLength) * 100;
    return Math.round(similarity);
  }

  // �🔍 Check if two names might be the same person (exact or fuzzy match)
  function areNamesSimilar(name1, name2, threshold = 70) {
    if (!name1 || !name2) return false;
    
    const norm1 = normalizeName(name1);
    const norm2 = normalizeName(name2);
    
    // Exact match after normalization
    if (norm1 === norm2) return true;
    
    // Check if the words are the same but in different order
    const words1 = norm1.split(" ");
    const words2 = norm2.split(" ");
    
    if (words1.length === words2.length && words1.every(word => words2.includes(word))) {
      return true;
    }
    
    // Fuzzy matching with similarity threshold
    const similarity = calculateSimilarity(name1, name2);
    return similarity >= threshold;
  }

  // 📊 Load all unique dancers from all competitions
  async function loadAllDancers() {
    setLoading(true);
    setMergeStatus("🔍 Loading all dancers from competitions...");
    
    const dancersMap = new Map();
    
    try {
      // Query old structure
      const oldQuery = collection(db, "competitions");
      const oldSnapshot = await getDocs(oldQuery);
      
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.results) {
          comp.results.forEach((result) => {
            const dancerName = result.dancerName;
            const studio = result.studio || "Unknown";
            const compId = doc.id;
            const compName = comp.name || compId;
            
            if (dancerName) {
              const key = dancerName; // Keep original name as key
              
              if (!dancersMap.has(key)) {
                dancersMap.set(key, {
                  name: dancerName,
                  studios: new Set(),
                  competitions: new Set(),
                  totalResults: 0,
                  sampleCompetitions: []
                });
              }
              
              const dancer = dancersMap.get(key);
              dancer.studios.add(studio);
              dancer.competitions.add(compId);
              dancer.totalResults++;
              
              if (dancer.sampleCompetitions.length < 3) {
                dancer.sampleCompetitions.push(compName);
              }
            }
          });
        }
      });
      
      // Query new structure
      const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
      
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          
          // Check direct competitions
          try {
            const directCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
            
            directCompsSnapshot.forEach((doc) => {
              const comp = doc.data();
              if (comp.results) {
                comp.results.forEach((result) => {
                  const dancerName = result.dancerName;
                  const studio = result.studio || "Unknown";
                  const compId = doc.id;
                  const compName = comp.name || compId;
                  
                  if (dancerName) {
                    const key = dancerName;
                    
                    if (!dancersMap.has(key)) {
                      dancersMap.set(key, {
                        name: dancerName,
                        studios: new Set(),
                        competitions: new Set(),
                        totalResults: 0,
                        sampleCompetitions: []
                      });
                    }
                    
                    const dancer = dancersMap.get(key);
                    dancer.studios.add(studio);
                    dancer.competitions.add(compId);
                    dancer.totalResults++;
                    
                    if (dancer.sampleCompetitions.length < 3) {
                      dancer.sampleCompetitions.push(compName);
                    }
                  }
                });
              }
            });
          } catch (error) {
            // No direct competitions
          }
          
          // Check dance-specific competitions
          try {
            const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
            
            for (const danceDoc of dancesSnapshot.docs) {
              const danceName = danceDoc.id;
              const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
              
              danceCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (comp.results) {
                  comp.results.forEach((result) => {
                    const dancerName = result.dancerName;
                    const studio = result.studio || "Unknown";
                    const compId = doc.id;
                    const compName = comp.name || compId;
                    
                    if (dancerName) {
                      const key = dancerName;
                      
                      if (!dancersMap.has(key)) {
                        dancersMap.set(key, {
                          name: dancerName,
                          studios: new Set(),
                          competitions: new Set(),
                          totalResults: 0,
                          sampleCompetitions: []
                        });
                      }
                      
                      const dancer = dancersMap.get(key);
                      dancer.studios.add(studio);
                      dancer.competitions.add(compId);
                      dancer.totalResults++;
                      
                      if (dancer.sampleCompetitions.length < 3) {
                        dancer.sampleCompetitions.push(compName);
                      }
                    }
                  });
                }
              });
            }
          } catch (error) {
            // No dance-specific competitions
          }
        }
      }
      
      // Convert to array and sort by total results
      const dancersArray = Array.from(dancersMap.values()).map(dancer => ({
        ...dancer,
        studios: Array.from(dancer.studios),
        competitions: Array.from(dancer.competitions)
      })).sort((a, b) => b.totalResults - a.totalResults);
      
      setAllDancers(dancersArray);
      
      // Find potential duplicates
      const duplicates = findPotentialDuplicates(dancersArray);
      setPotentialDuplicates(duplicates);
      
      setMergeStatus(`✅ Loaded ${dancersArray.length} unique dancers. Found ${duplicates.length} potential duplicate groups.`);
      
    } catch (error) {
      console.error("❌ Error loading dancers:", error);
      setMergeStatus("❌ Error loading dancers: " + error.message);
    }
    
    setLoading(false);
  }

  // 🔍 Find potential duplicates based on name similarity
  function findPotentialDuplicates(dancers) {
    const duplicateGroups = [];
    const processed = new Set();
    
    for (let i = 0; i < dancers.length; i++) {
      if (processed.has(i)) continue;
      
      const dancer1 = dancers[i];
      const group = [{
        ...dancer1,
        similarity: 100 // The dancer is 100% similar to themselves
      }];
      processed.add(i);
      
      for (let j = i + 1; j < dancers.length; j++) {
        if (processed.has(j)) continue;
        
        const dancer2 = dancers[j];
        
        if (areNamesSimilar(dancer1.name, dancer2.name, 70)) {
          const similarity = calculateSimilarity(dancer1.name, dancer2.name);
          group.push({
            ...dancer2,
            similarity: similarity
          });
          processed.add(j);
        }
      }
      
      // Only include groups with more than one dancer (potential duplicates)
      if (group.length > 1) {
        // Sort group by similarity (highest first)
        group.sort((a, b) => b.similarity - a.similarity);
        duplicateGroups.push(group);
      }
    }
    
    // Sort groups by the highest similarity within each group
    duplicateGroups.sort((a, b) => {
      const maxSimilarityA = Math.max(...a.slice(1).map(d => d.similarity));
      const maxSimilarityB = Math.max(...b.slice(1).map(d => d.similarity));
      return maxSimilarityB - maxSimilarityA;
    });
    
    return duplicateGroups;
  }

  // 🎯 Filter dancers based on search term
  function getFilteredDancers() {
    let dancers = showOnlyDuplicates ? potentialDuplicates.flat() : allDancers;
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      dancers = dancers.filter(dancer => 
        dancer.name.toLowerCase().includes(term) ||
        dancer.studios.some(studio => studio.toLowerCase().includes(term))
      );
    }
    
    return dancers;
  }

  // ✅ Handle dancer selection for merging
  function handleDancerSelect(dancer) {
    const isSelected = selectedDancers.find(d => d.name === dancer.name);
    
    if (isSelected) {
      setSelectedDancers(selectedDancers.filter(d => d.name !== dancer.name));
    } else {
      setSelectedDancers([...selectedDancers, dancer]);
    }
  }

  // 🔄 Merge selected dancers
  // dancerList: optional explicit list (for per-group merges); falls back to selectedDancers
  // groupIndex: optional — if provided, removes the merged group from potentialDuplicates locally
  async function mergeDancers(dancerList, groupIndex) {
    const toMerge = dancerList || selectedDancers;
    if (toMerge.length < 2) {
      setMergeStatus("❌ Please select at least 2 dancers to merge.");
      return;
    }
    
    if (!user) {
      setMergeStatus("❌ Please log in to perform merge operations.");
      return;
    }
    
    const primaryDancer = toMerge[0];
    const secondaryDancers = toMerge.slice(1);
    
    const newName = prompt(
      `Enter the correct name for the merged profile:\n\nCurrent variations:\n${toMerge.map(d => `• ${d.name}`).join('\n')}`,
      primaryDancer.name
    );
    
    if (!newName || !newName.trim()) {
      setMergeStatus("❌ Merge cancelled - no name provided.");
      return;
    }
    
    setMergeStatus("⏳ Merging dancers...");
    
    try {
      const batch = writeBatch(db);
      let updatedCompetitions = 0;
      
      // Track all changes for history so we can unmerge later
      // changedDocs: [{ path, resultPatches: [{index, originalName}] }]
      const changedDocs = [];

      function applyRename(docRef, comp) {
        let hasUpdates = false;
        const patches = [];
        const roundDataPatches = [];

        // Patch results[]
        if (comp.results) {
          comp.results.forEach((result, idx) => {
            const matchingDancer = secondaryDancers.find(d => d.name === result.dancerName);
            if (matchingDancer) {
              patches.push({ index: idx, originalName: result.dancerName });
              result.dancerName = newName.trim();
              hasUpdates = true;
            }
          });
        }

        // Also patch roundData[] — same structure, same dancerName field
        if (comp.roundData) {
          comp.roundData.forEach((rd, idx) => {
            const matchingDancer = secondaryDancers.find(d => d.name === rd.dancerName);
            if (matchingDancer) {
              roundDataPatches.push({ index: idx, originalName: rd.dancerName });
              rd.dancerName = newName.trim();
              hasUpdates = true;
            }
          });
        }

        if (hasUpdates) {
          const updatePayload = { results: comp.results };
          if (comp.roundData) updatePayload.roundData = comp.roundData;
          batch.update(docRef, updatePayload);
          changedDocs.push({ path: docRef.path, patches, roundDataPatches });
          updatedCompetitions++;
        }
      }

      // Update all competitions in old structure
      const oldSnapshot = await getDocs(collection(db, "competitions"));
      oldSnapshot.forEach((compDoc) => {
        applyRename(compDoc.ref, compDoc.data());
      });
      
      // Update all competitions in new structure
      const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
      
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          
          // Update direct competitions
          try {
            const directCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
            directCompsSnapshot.forEach((compDoc) => {
              applyRename(compDoc.ref, compDoc.data());
            });
          } catch (error) { /* No direct competitions */ }
          
          // Update dance-specific competitions
          try {
            const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
            for (const danceDoc of dancesSnapshot.docs) {
              const danceName = danceDoc.id;
              const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
              danceCompsSnapshot.forEach((compDoc) => {
                applyRename(compDoc.ref, compDoc.data());
              });
            }
          } catch (error) { /* No dance-specific competitions */ }
        }
      }
      
      // Commit all updates
      await batch.commit();

      // ── Migrate dancerProfiles and dancers docs ──────────────────────────────
      // Both collections use dancer name as the document ID, so if the name
      // changes we must copy the primary doc to the new ID and delete the old.
      // Secondary dancer profile docs are always deleted (folded into primary).
      const profileCollections = ["dancerProfiles", "dancers"];
      const primaryProfileData = {};   // cache primary doc data per collection

      for (const colName of profileCollections) {
        // 1. Read primary dancer's existing profile doc
        const primaryRef = doc(db, colName, primaryDancer.name);
        const primarySnap = await getDoc(primaryRef);

        if (newName.trim() !== primaryDancer.name) {
          // Name changed — copy to new doc ID, delete old
          if (primarySnap.exists()) {
            primaryProfileData[colName] = primarySnap.data();
            await setDoc(doc(db, colName, newName.trim()), primarySnap.data());
            await deleteDoc(primaryRef);
          }
        }

        // 2. Delete every secondary dancer's profile doc
        for (const sec of secondaryDancers) {
          const secRef = doc(db, colName, sec.name);
          const secSnap = await getDoc(secRef);
          if (secSnap.exists()) {
            await deleteDoc(secRef);
          }
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // ── Merge profileVisits — add secondary visit counts into primary ───────────
      {
        const primaryOldVisitsRef = doc(db, "profileVisits", `dancer_${primaryDancer.name}`);
        const primaryOldVisitsSnap = await getDoc(primaryOldVisitsRef);
        let totalVisits = primaryOldVisitsSnap.exists() ? (primaryOldVisitsSnap.data().totalVisits || 0) : 0;
        const primaryVisitsData = primaryOldVisitsSnap.exists() ? primaryOldVisitsSnap.data() : null;

        for (const sec of secondaryDancers) {
          const secVisitsRef = doc(db, "profileVisits", `dancer_${sec.name}`);
          const secVisitsSnap = await getDoc(secVisitsRef);
          if (secVisitsSnap.exists()) {
            totalVisits += secVisitsSnap.data().totalVisits || 0;
            try { await deleteDoc(secVisitsRef); } catch (_) {}
          }
        }

        const finalVisitsDocId = `dancer_${newName.trim()}`;
        const finalVisitsRef = doc(db, "profileVisits", finalVisitsDocId);

        if (newName.trim() !== primaryDancer.name) {
          // Name changed — delete old doc and write new one
          if (primaryOldVisitsSnap.exists()) {
            try { await deleteDoc(primaryOldVisitsRef); } catch (_) {}
          }
          await setDoc(finalVisitsRef, {
            profileName: newName.trim(),
            profileType: "dancer",
            totalVisits,
            recentVisits: primaryVisitsData?.recentVisits || [],
            firstVisit: primaryVisitsData?.firstVisit || null,
            lastVisit: primaryVisitsData?.lastVisit || null,
          });
        } else if (totalVisits > 0) {
          if (primaryOldVisitsSnap.exists()) {
            await updateDoc(primaryOldVisitsRef, { totalVisits });
          } else {
            await setDoc(primaryOldVisitsRef, {
              profileName: primaryDancer.name,
              profileType: "dancer",
              totalVisits,
              recentVisits: [],
              firstVisit: null,
              lastVisit: null,
            });
          }
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // ── Clean up stale dancerRankingsCache entries ───────────────────────────
      // Delete the cache docs for all secondary names (they no longer exist)
      // and also for the primary name if it was renamed (the new name has no cache yet).
      // The next nightly cache run will rebuild the merged profile with correct badges.
      const cacheNamesToDelete = secondaryDancers.map(d => d.name);
      if (newName.trim() !== primaryDancer.name) {
        cacheNamesToDelete.push(primaryDancer.name); // old name cache is now stale too
      }
      for (const staleName of cacheNamesToDelete) {
        try {
          await deleteDoc(doc(db, "dancerRankingsCache", staleName));
        } catch (_) { /* best-effort */ }
      }
      // ────────────────────────────────────────────────────────────────────────

      // Save merge history record so we can unmerge later
      if (changedDocs.length > 0) {
        await addDoc(collection(db, "mergeHistory"), {
          mergedAt: serverTimestamp(),
          mergedBy: user.email || user.uid,
          newName: newName.trim(),
          originalNames: secondaryDancers.map(d => d.name),
          primaryName: primaryDancer.name,
          changedDocs,
        });
      }
      
      setMergeStatus(`✅ Successfully merged ${toMerge.length} dancers into "${newName}". Updated ${updatedCompetitions} competitions.`);

      // ── Update local state without a full Firestore reload ──────────────────────────
      const mergedNames = new Set(toMerge.map(d => d.name));
      const trimmedNewName = newName.trim();

      setAllDancers(prev => {
        // Merge the toMerge entries into one, keep everything else
        const merged = {
          name: trimmedNewName,
          studios: [...new Set(toMerge.flatMap(d => d.studios))],
          competitions: [...new Set(toMerge.flatMap(d => d.competitions))],
          totalResults: toMerge.reduce((s, d) => s + d.totalResults, 0),
          sampleCompetitions: toMerge[0].sampleCompetitions || [],
        };
        const rest = prev.filter(d => !mergedNames.has(d.name));
        return [...rest, merged].sort((a, b) => b.totalResults - a.totalResults);
      });

      setPotentialDuplicates(prev => {
        if (groupIndex != null) {
          // Remove the whole group that was just merged
          return prev.filter((_, i) => i !== groupIndex);
        }
        // Fallback: remove all dancers that were part of this merge from every group
        return prev
          .map(group => group.filter(d => !mergedNames.has(d.name)))
          .filter(group => group.length > 1);
      });

      // Clear group selection for this group
      if (groupIndex != null) {
        setGroupSelections(prev => {
          const next = { ...prev };
          delete next[groupIndex];
          return next;
        });
      }
      setSelectedDancers([]);
      // ──────────────────────────────────────────────────────────────────────────
      
    } catch (error) {
      console.error("❌ Error merging dancers:", error);
      setMergeStatus("❌ Error merging dancers: " + error.message);
    }
  }

  // 🔙 Unmerge: reverse a previously saved merge
  async function unmerge(historyEntry) {
    if (!user) {
      setMergeStatus("❌ Please log in to perform unmerge operations.");
      return;
    }
    const confirmMsg = `Unmerge "${historyEntry.newName}" back into:\n${historyEntry.originalNames.map(n => `• ${n}`).join("\n")}\n\nThis will restore the original names in ${historyEntry.changedDocs?.length ?? 0} competition documents.`;
    if (!window.confirm(confirmMsg)) return;

    setMergeStatus("⏳ Unmerging...");
    try {
      const batch = writeBatch(db);
      let restoredDocs = 0;

      for (const { path, patches, roundDataPatches } of (historyEntry.changedDocs || [])) {
        // Fetch the current document
        const docRef = doc(db, path);
        const snap = await getDoc(docRef);
        if (!snap.exists()) continue;
        const compData = snap.data();
        if (!compData.results) continue;

        let changed = false;

        // Restore results[]
        for (const { index, originalName } of (patches || [])) {
          if (compData.results[index] && compData.results[index].dancerName === historyEntry.newName) {
            compData.results[index].dancerName = originalName;
            changed = true;
          }
        }

        // Restore roundData[]
        if (compData.roundData && roundDataPatches?.length > 0) {
          for (const { index, originalName } of roundDataPatches) {
            if (compData.roundData[index] && compData.roundData[index].dancerName === historyEntry.newName) {
              compData.roundData[index].dancerName = originalName;
              changed = true;
            }
          }
        }

        if (changed) {
          const updatePayload = { results: compData.results };
          if (compData.roundData) updatePayload.roundData = compData.roundData;
          batch.update(docRef, updatePayload);
          restoredDocs++;
        }
      }

      await batch.commit();

      // ── Restore dancerProfiles / dancers doc IDs ─────────────────────────────
      // If the merge renamed the primary dancer, move the profile doc back.
      if (historyEntry.newName !== historyEntry.primaryName) {
        const profileCollections = ["dancerProfiles", "dancers"];
        for (const colName of profileCollections) {
          const newRef = doc(db, colName, historyEntry.newName);
          const newSnap = await getDoc(newRef);
          if (newSnap.exists()) {
            // Restore to originalPrimaryName
            await setDoc(doc(db, colName, historyEntry.primaryName), newSnap.data());
            await deleteDoc(newRef);
          }
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // Delete the history record
      await deleteDoc(doc(db, "mergeHistory", historyEntry.id));

      setMergeHistory(prev => prev.filter(h => h.id !== historyEntry.id));
      setMergeStatus(`✅ Unmerge complete. Restored original names in ${restoredDocs} competition documents.`);
      loadAllDancers();
    } catch (error) {
      console.error("❌ Error unmerging:", error);
      setMergeStatus("❌ Error unmerging: " + error.message);
    }
  }

  if (loading) {
    return (
      <div className="merge-dancers-container">
        <h2>🔄 Merge Dancers</h2>
        <p>{mergeStatus}</p>
      </div>
    );
  }

  const filteredDancers = getFilteredDancers();

  return (
    <div className="merge-dancers-container">
      <h2>🔄 Merge / Unmerge Dancers</h2>
      <p className="description">
        This tool helps you identify and merge duplicate dancer profiles that may have been 
        created due to name variations (surname/name order, capitalization, etc.). 
        Uses fuzzy matching to detect names with 70%+ similarity for misspellings and typos.
      </p>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", borderBottom: "2px solid #ddd", paddingBottom: "0.5rem" }}>
        <button
          onClick={() => setActiveTab("merge")}
          style={{ padding: "0.4rem 1.2rem", borderRadius: "6px 6px 0 0", border: activeTab === "merge" ? "2px solid #3a3a5c" : "2px solid transparent",
            background: activeTab === "merge" ? "#3a3a5c" : "#e8e8e8", color: activeTab === "merge" ? "#fff" : "#333", fontWeight: "bold", cursor: "pointer" }}>
          🔀 Merge
        </button>
        <button
          onClick={() => setActiveTab("scripts")}
          style={{ padding: "0.4rem 1.2rem", borderRadius: "6px 6px 0 0", border: activeTab === "scripts" ? "2px solid #1565c0" : "2px solid transparent",
            background: activeTab === "scripts" ? "#1565c0" : "#e8e8e8", color: activeTab === "scripts" ? "#fff" : "#333", fontWeight: "bold", cursor: "pointer" }}>
          🔤 Greek ⇄ English
        </button>
        <button
          onClick={() => setActiveTab("unmerge")}
          style={{ padding: "0.4rem 1.2rem", borderRadius: "6px 6px 0 0", border: activeTab === "unmerge" ? "2px solid #b33" : "2px solid transparent",
            background: activeTab === "unmerge" ? "#b33" : "#e8e8e8", color: activeTab === "unmerge" ? "#fff" : "#333", fontWeight: "bold", cursor: "pointer" }}>
          ↩️ Unmerge History
        </button>
      </div>
      
      {mergeStatus && (
        <div className={`status-message ${mergeStatus.startsWith('❌') ? 'error' : mergeStatus.startsWith('✅') ? 'success' : 'info'}`}>
          {mergeStatus}
        </div>
      )}

      {/* ─── MERGE TAB ─── */}
      {activeTab === "merge" && (<>
      
      <div className="controls">
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search by dancer name or studio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showOnlyDuplicates}
              onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
            />
            Show only potential duplicates
          </label>
        </div>
        
        <div className="merge-controls">
          {selectedDancers.length > 0 && (
            <p style={{ margin: "0 0 0.4rem", fontSize: "0.88rem" }}>
              Selected for merge: <strong>{selectedDancers.length}</strong> dancer{selectedDancers.length > 1 ? "s" : ""}
              {selectedDancers.length > 0 && (
                <span style={{ color: "#888", fontWeight: "normal", marginLeft: "6px" }}>
                  — <span style={{ color: "#2e7d32", fontWeight: "bold" }}>①&nbsp;{selectedDancers[0]?.name}</span>
                  {" is primary (will keep its name by default)"}
                </span>
              )}
            </p>
          )}
          {selectedDancers.length < 1 && (
            <p style={{ margin: "0 0 0.4rem", fontSize: "0.88rem", color: "#888" }}>No dancers selected — click any card to select</p>
          )}
          {selectedDancers.length > 1 && (
            <button
              onClick={() => mergeDancers()}
              className="merge-button"
              disabled={!user}
            >
              Merge Selected Dancers
            </button>
          )}
          {selectedDancers.length > 0 && (
            <button
              onClick={() => setSelectedDancers([])}
              className="clear-button"
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>
      
      {!user && (
        <div className="warning">
          ⚠️ You must be logged in to perform merge operations.
        </div>
      )}
      
      <div className="stats">
        <p>
          📊 Total unique dancers: {allDancers.length} | 
          Potential duplicate groups: {potentialDuplicates.length} | 
          Showing: {filteredDancers.length} dancers
        </p>
      </div>
      
      <div className="dancers-grid">
        {showOnlyDuplicates && potentialDuplicates.length > 0 ? (
          potentialDuplicates.map((group, groupIndex) => {
            const sel = groupSelections[groupIndex] || new Set();
            const selectedInGroup = group.filter(d => sel.has(d.name));
            const canMergeGroup = selectedInGroup.length >= 2;

            const toggleInGroup = (dancer) => {
              // Update per-group selection
              setGroupSelections(prev => {
                const current = new Set(prev[groupIndex] || []);
                current.has(dancer.name) ? current.delete(dancer.name) : current.add(dancer.name);
                return { ...prev, [groupIndex]: current };
              });
              // Also update global selectedDancers so "Merge Selected Dancers" works cross-group
              handleDancerSelect(dancer);
            };

            return (
              <div key={groupIndex} className="duplicate-group">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.4rem" }}>
                  <h4 style={{ margin: 0 }}>Potential Duplicate Group {groupIndex + 1}</h4>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    {canMergeGroup && (
                      <button
                        onClick={() => mergeDancers(selectedInGroup, groupIndex)}
                        disabled={!user}
                        style={{ padding: "0.3rem 0.9rem", background: "#2e7d32", color: "#fff", border: "none", borderRadius: "5px", cursor: user ? "pointer" : "not-allowed", fontWeight: "bold", fontSize: "0.85rem" }}
                      >
                        🔀 Merge {selectedInGroup.length} selected
                      </button>
                    )}
                    {sel.size > 0 && (
                      <button
                        onClick={() => setGroupSelections(prev => ({ ...prev, [groupIndex]: new Set() }))}
                        style={{ padding: "0.25rem 0.7rem", background: "none", border: "1px solid #aaa", borderRadius: "5px", cursor: "pointer", fontSize: "0.8rem", color: "#555" }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="group-dancers">
                  {group.map((dancer, index) => (
                    <DancerCard 
                      key={`${groupIndex}-${index}`}
                      dancer={dancer}
                      isSelected={sel.has(dancer.name)}
                      selectionOrder={selectedDancers.findIndex(d => d.name === dancer.name)}
                      onSelect={() => toggleInGroup(dancer)}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="dancers-list">
            {filteredDancers.map((dancer, index) => (
              <DancerCard 
                key={index}
                dancer={dancer}
                isSelected={selectedDancers.some(d => d.name === dancer.name)}
                selectionOrder={selectedDancers.findIndex(d => d.name === dancer.name)}
                onSelect={() => handleDancerSelect(dancer)}
              />
            ))}
          </div>
        )}
      </div>
      
      {filteredDancers.length === 0 && (
        <div className="no-results">
          {showOnlyDuplicates 
            ? "🎉 No potential duplicates found!" 
            : "No dancers match your search criteria."}
        </div>
      )}
      </>)}

      {/* ─── GREEK ⇄ ENGLISH TAB ─── */}
      {activeTab === "scripts" && (
        <MergeScriptsTab
          allDancers={allDancers}
          user={user}
          onMerge={(ordered) => mergeDancers(ordered, null)}
        />
      )}

      {/* ─── UNMERGE TAB ─── */}
      {activeTab === "unmerge" && (
        <div>
          {!user && (
            <div className="warning">⚠️ You must be logged in to perform unmerge operations.</div>
          )}
          {historyLoading ? (
            <p>⏳ Loading merge history…</p>
          ) : mergeHistory.length === 0 ? (
            <div className="no-results">📭 No merge history found. Merges performed from now on will be recorded here.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ background: "#3a3a5c", color: "#fff" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Date</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Merged Into</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Original Names</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Docs Changed</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>By</th>
                  <th style={{ padding: "8px 10px" }}></th>
                </tr>
              </thead>
              <tbody>
                {mergeHistory.map((entry, i) => {
                  const date = entry.mergedAt?.toDate
                    ? entry.mergedAt.toDate().toLocaleString()
                    : "—";
                  return (
                    <tr key={entry.id} style={{ background: i % 2 === 0 ? "#fff" : "#f5f5f5", borderBottom: "1px solid #ddd" }}>
                      <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{date}</td>
                      <td style={{ padding: "7px 10px", fontWeight: "bold" }}>{entry.newName}</td>
                      <td style={{ padding: "7px 10px" }}>
                        {(entry.originalNames || []).map((n, j) => (
                          <span key={j} style={{ display: "inline-block", background: "#e8f4fd", border: "1px solid #b3d9f7", borderRadius: "10px", padding: "1px 8px", margin: "2px 3px 2px 0", fontSize: "0.82rem" }}>{n}</span>
                        ))}
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>{entry.changedDocs?.length ?? "—"}</td>
                      <td style={{ padding: "7px 10px", color: "#666", fontSize: "0.8rem" }}>{entry.mergedBy || "—"}</td>
                      <td style={{ padding: "7px 10px" }}>
                        <button
                          onClick={() => unmerge(entry)}
                          disabled={!user}
                          style={{ background: "#c0392b", color: "#fff", border: "none", borderRadius: "5px", padding: "4px 12px", cursor: user ? "pointer" : "not-allowed", fontWeight: "bold", opacity: user ? 1 : 0.5 }}>
                          ↩️ Unmerge
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// 🎭 Individual dancer card component
function DancerCard({ dancer, isSelected, selectionOrder, onSelect }) {
  const orderNum = selectionOrder >= 0 ? selectionOrder + 1 : null;
  const ORDER_BADGES = ["①","②","③","④","⑤","⑥","⑦","⑧"];
  return (
    <div 
      className={`dancer-card ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
    >
      <div className="dancer-header">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <DancerThumbnail 
            dancerName={dancer.name} 
            size={36} 
            showPlaceholder={true}
          />
          <h4>{dancer.name}</h4>
        </div>
        <div className="header-info">
          {dancer.similarity !== undefined && dancer.similarity < 100 && (
            <div className="similarity-badge" title={`${dancer.similarity}% similar to group reference`}>
              {dancer.similarity}% match
            </div>
          )}
          <div className="selection-indicator" style={{ fontSize: "1.3rem", minWidth: "1.6rem", textAlign: "center" }}>
            {isSelected
              ? (orderNum ? <span title={orderNum === 1 ? "Primary — will be the main profile" : `Merged into primary`} style={{ color: orderNum === 1 ? "#2e7d32" : "#1565c0" }}>{ORDER_BADGES[orderNum - 1] || "✅"}</span>
                         : "✅")
              : "⬜"}
          </div>
        </div>
      </div>
      
      <div className="dancer-details">
        <p><strong>Studios:</strong> {dancer.studios.join(', ')}</p>
        <p><strong>Competitions:</strong> {dancer.competitions.length}</p>
        <p><strong>Total Results:</strong> {dancer.totalResults}</p>
        
        {dancer.sampleCompetitions.length > 0 && (
          <div className="sample-competitions">
            <strong>Recent competitions:</strong>
            <ul>
              {dancer.sampleCompetitions.map((comp, index) => (
                <li key={index}>{comp}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
